import type { Node as ProseMirrorNode } from '@tiptap/pm/model';
import { closeHistory } from '@tiptap/pm/history';
import { Plugin, PluginKey, type EditorState, type Transaction } from '@tiptap/pm/state';
import { ReplaceStep } from '@tiptap/pm/transform';
import type { EditorView } from '@tiptap/pm/view';
import { yUndoPluginKey } from '@tiptap/y-tiptap';

import { decideAutocorrect } from './autocorrect-rules';
import { normaliseWord } from './spell-check-protocol';
import { checkableBlockText, tokenizeWords } from './spell-tokenize';

/**
 * Word-style AutoCorrect. When a person finishes a word (space, punctuation or Enter) and it is an obvious
 * typo, it is fixed straight away as its own ordinary edit: Ctrl+Z restores what they typed, and tracked
 * changes and live editing see it exactly like typing. A fix the person undoes is never reapplied to that
 * word again this session. Anything unclear is left as an underlined suggestion.
 */
export interface AutocorrectPluginOptions {
  editable: () => boolean;
  /** Spell checking is switched on for this editor. */
  isEnabled: (state: EditorState) => boolean;
  /** Ignored this session or in the person's own dictionary. */
  isWordAccepted: (word: string) => boolean;
  /** Local dictionary verdict for a normalised word, if checked already. */
  verdict: (word: string) => boolean | undefined;
}

interface Candidate { id: number; from: number; to: number; word: string; sentenceStart: boolean }
/** An automatic fix still showing its replacement (positions follow later edits). */
interface Correction { from: number; to: number; original: string; replacement: string }
interface AutocorrectState { candidate: Candidate | null; corrections: readonly Correction[] }

export type AutocorrectMeta = { type: 'applied'; correction: Correction };

export const autocorrectKey = new PluginKey<AutocorrectState>('documentAutocorrect');

/** Characters that finish a word. Apostrophes and hyphens do not (don't, well-known). */
const BOUNDARY = /^[\s.,;:!?)\]}"\u201d\u2026]$/u;
const SENTENCE_END = /[.!?\u2026]$/u;
const LEADING_OPENERS = /[\s"'\u201c\u2018([{]+$/u;
const SKIPPED_META = ['history$', 'y-sync$', 'y-undo$', 'paste', 'appendedTransaction', 'documentReplacement'];
const MAX_TRACKED_CORRECTIONS = 20;

/* Words whose automatic fix was undone, for every editor on the page until it is reloaded. */
const revertedWords = new Set<string>();
const revertKey = (word: string) => normaliseWord(word).toLowerCase();

export function isAutocorrectReverted(word: string): boolean { return revertedWords.has(revertKey(word)); }
export function resetAutocorrectSession(): void { revertedWords.clear(); }

let nextId = 1;

function skipped(tr: Transaction): boolean {
  if (tr.getMeta(autocorrectKey)) return true;
  const uiEvent = tr.getMeta('uiEvent');
  return uiEvent === 'paste' || uiEvent === 'drop' || SKIPPED_META.some(key => tr.getMeta(key) !== undefined);
}

/** Position just after the finished word, when this edit is typing a word boundary. */
function boundaryPosition(tr: Transaction): number | null {
  const step = tr.steps[0];
  if (!(step instanceof ReplaceStep) || step.from !== step.to) return null;
  const { content } = step.slice;
  const text = content.textBetween(0, content.size, '\n');
  const typed = text.length === 1 && BOUNDARY.test(text);
  const enter = !text && content.childCount >= 2 && step.slice.openStart > 0;
  if (!typed && !enter) return null;
  return tr.mapping.slice(1).map(step.from, -1);
}

/** The finished word ending exactly at `end`, if it is a checkable word. */
export function finishedWordAt(doc: ProseMirrorNode, end: number): Omit<Candidate, 'id'> | null {
  if (end < 0 || end > doc.content.size) return null;
  const $end = doc.resolve(end);
  if (!$end.parent.isTextblock) return null;
  const text = checkableBlockText($end.parent);
  const offset = end - $end.start();
  if (text.length !== $end.parent.content.size) return null;
  const token = tokenizeWords(text).find(item => item.end === offset);
  if (!token) return null;
  const before = text.slice(0, token.start).replace(LEADING_OPENERS, '');
  const from = $end.start() + token.start;
  return { from, to: from + (token.end - token.start), word: text.slice(token.start, token.end), sentenceStart: !before || SENTENCE_END.test(before) };
}

/** Follows earlier fixes through an edit; a fix that now shows the typed word again was undone. */
function trackCorrections(tr: Transaction, corrections: readonly Correction[]): Correction[] {
  const kept: Correction[] = [];
  for (const correction of corrections) {
    const from = tr.mapping.map(correction.from, -1);
    const to = tr.mapping.map(correction.to, 1);
    if (to <= from || to > tr.doc.content.size) continue;
    const text = tr.doc.textBetween(from, to);
    const undone = tr.doc.textBetween(from, Math.min(from + correction.original.length, tr.doc.content.size));
    if (text === correction.original || undone === correction.original) revertedWords.add(revertKey(correction.original));
    else if (text === correction.replacement) kept.push({ ...correction, from, to });
  }
  return kept;
}

interface UndoManagerLike { stopCapturing: () => void }
function liveEditingUndo(state: EditorState): UndoManagerLike | null {
  const value: unknown = yUndoPluginKey.getState(state);
  if (!value || typeof value !== 'object' || !('undoManager' in value)) return null;
  const manager: unknown = value.undoManager;
  return manager && typeof manager === 'object' && 'stopCapturing' in manager && typeof manager.stopCapturing === 'function'
    ? manager as UndoManagerLike : null;
}

function correct(view: EditorView, candidate: Candidate, options: AutocorrectPluginOptions): void {
  const { state } = view;
  if (!options.editable() || !options.isEnabled(state)) return;
  if (candidate.to > state.doc.content.size || state.doc.textBetween(candidate.from, candidate.to) !== candidate.word) return;
  const replacement = decideAutocorrect({
    word: candidate.word,
    sentenceStart: candidate.sentenceStart,
    accepted: options.isWordAccepted(candidate.word),
    reverted: isAutocorrectReverted(candidate.word),
    correct: options.verdict(normaliseWord(candidate.word)),
  });
  if (!replacement || replacement === candidate.word) return;

  const undo = liveEditingUndo(state);
  undo?.stopCapturing();
  const tr = closeHistory(state.tr);
  const marks = state.doc.resolve(candidate.from).marksAcross(state.doc.resolve(candidate.to)) ?? [];
  tr.replaceWith(candidate.from, candidate.to, state.schema.text(replacement, marks));
  const to = candidate.from + replacement.length;
  tr.setMeta(autocorrectKey, { type: 'applied', correction: { from: candidate.from, to, original: candidate.word, replacement } } satisfies AutocorrectMeta);
  view.dispatch(tr);
  // The next keystrokes start a new undo step, so Ctrl+Z undoes only the fix.
  undo?.stopCapturing();
}

export function createAutocorrectPlugin(options: AutocorrectPluginOptions): Plugin<AutocorrectState> {
  return new Plugin<AutocorrectState>({
    key: autocorrectKey,
    state: {
      init: () => ({ candidate: null, corrections: [] }),
      apply(tr, value, _oldState, newState) {
        let corrections = tr.docChanged ? trackCorrections(tr, value.corrections) : value.corrections;
        const meta = tr.getMeta(autocorrectKey) as AutocorrectMeta | undefined;
        if (meta?.type === 'applied') corrections = [...corrections, meta.correction].slice(-MAX_TRACKED_CORRECTIONS);
        let candidate: Candidate | null = null;
        if (tr.docChanged && !skipped(tr) && options.editable() && options.isEnabled(newState)) {
          const end = boundaryPosition(tr);
          const word = end === null ? null : finishedWordAt(tr.doc, end);
          if (word) candidate = { ...word, id: nextId++ };
        }
        return candidate === value.candidate && corrections === value.corrections ? value : { candidate, corrections };
      },
    },
    view() {
      let handled = 0;
      let destroyed = false;
      return {
        update(view) {
          const candidate = autocorrectKey.getState(view.state)?.candidate;
          if (!candidate || candidate.id === handled) return;
          handled = candidate.id;
          // After the keystroke's own update finishes, as a separate edit (so it is undone on its own).
          queueMicrotask(() => { if (!destroyed && !view.isDestroyed) correct(view, candidate, options); });
        },
        destroy() { destroyed = true; },
      };
    },
  });
}
